import PropTypes, { func } from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import {
  Button,
  makeStyles,
  shorthands,
  mergeClasses,
  Body1,
  Field,
  RadioGroup,
  Radio,
  Checkbox,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import { AdsStudioPanelContainer, ClarityTags, useClarityTags } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { ImageCropWithBoundingBox } from '@bingads-webui-campaign-react/image-crop-with-bounding-box';

const useStyles = makeStyles({
  buttonWrapper: {
    ...shorthands.padding('16px', '24px', '24px'),
  },
  button: {
    minWidth: 'auto',
    ...shorthands.padding('6px', '12px'),
    marginRight: '6px',
  },
  aspectRatio: {
    marginTop: tokens.spacingVerticalXXL,
  },
  background: {
    display: 'flex',
    flexDirection: 'column',
  },
  imageEditorContainer: {
    display: 'flex',
    marginTop: tokens.spacingVerticalXXL,
    marginBottom: tokens.spacingVerticalXXL,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(129, 129, 129, 0.20)',
    boxShadow: tokens.shadow2,

    '& .cropper-sub-container': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  spinner: {
    height: '400px',
  },
});

const MaxCropHeight = 600;

const LogoFormat = {
  square: 'square',
  landscape: 'landscape',
};

export const BrandKitLogoEditPanel = ({
  i18n,
  isOpen,
  dismiss,
  format,
  minLogoSize,
  lightBackground,
  hasLandScapeLimit,
  hasSquareLimit,
  showBackgroundSettings,
  darkBackground,
  image,
  cropSettings,
  onChange,
}) => {
  const classes = useStyles();
  const [aspectRatio, setAspectRatio] = React.useState(format);
  const [useInLightBackground, setUseInLightBackground] = React.useState(lightBackground);
  const [useInDarkBackground, setUseInDarkBackground] = React.useState(darkBackground);
  const [currentCropSettings, setCurrentCropSettings] = React.useState(cropSettings);
  const MinLogoSize = minLogoSize || 128;
  const maxCropHeight = useMemo(() => {
    if (image) {
      return Math.min(image.Height, MaxCropHeight);
    }
    return MaxCropHeight;
  }, [image]);

  const landscapeEnabled = useMemo(() => {
    if (image) {
      return image.Width >= MinLogoSize * 4;
    }
    if (format === LogoFormat.landscape || hasLandScapeLimit) {
      return true;
    }
    return false;
  }, [MinLogoSize, format, hasLandScapeLimit, image]);

  const sqaureEnabled = useMemo(() => {
    if (format === LogoFormat.square || hasSquareLimit) {
      return true;
    }
    return false;
  }, [format, hasSquareLimit]);

  const isValid = useMemo(
    () => !showBackgroundSettings || useInLightBackground || useInDarkBackground,
    [showBackgroundSettings, useInLightBackground, useInDarkBackground]
  );

  const updateAspectRation = useCallback((value) => {
    if (value === LogoFormat.landscape) {
      if ((currentCropSettings.SourceX + (currentCropSettings.SourceWidth * 4)) < image.Width) {
        setCurrentCropSettings({
          ...currentCropSettings,
          SourceWidth: currentCropSettings.SourceHeight * 4,
        });
      } else if ((currentCropSettings.SourceWidth * 4) < image.Width) {
        setCurrentCropSettings({
          ...currentCropSettings,
          SourceWidth: currentCropSettings.SourceHeight * 4,
          SourceX: image.Width - (currentCropSettings.SourceHeight * 4),
        });
      } else {
        const height = Math.floor(image.Width / 4);
        setCurrentCropSettings({
          ...currentCropSettings,
          SourceX: 0,
          SourceWidth: height * 4,
          SourceHeight: height,
        });
      }
    } else {
      setCurrentCropSettings({
        ...currentCropSettings,
        SourceWidth: currentCropSettings.SourceHeight,
      });
    }
    setAspectRatio(value);
  }, [setAspectRatio, currentCropSettings, setCurrentCropSettings, image]);

  useClarityTags([
    ClarityTags.LaunchedAdsStudio,
    ClarityTags.EditBrandKitLogo,
  ]);

  const onRenderFooterContent = useCallback(() => (
    <div className={mergeClasses('footer', classes.buttonWrapper)}>
      <Button
        className={mergeClasses('update', classes.button)}
        appearance="primary"
        disabled={!image || !isValid}
        onClick={() => onChange({
          cropSettings: currentCropSettings,
          lightBackground: useInLightBackground,
          darkBackground: useInDarkBackground,
          aspectRatio,
          })}
      >
        {i18n.getString(_TL_('Update logo'))}
      </Button>
      <Button className={mergeClasses('cancel', classes.button)} onClick={dismiss}>
        {i18n.getString(_TL_('Cancel'))}
      </Button>
    </div>
  ), [
    classes.buttonWrapper,
    classes.button,
    i18n,
    dismiss,
    image,
    isValid,
    onChange,
    currentCropSettings,
    useInLightBackground,
    useInDarkBackground,
    aspectRatio]);

  return (
    <AdsStudioPanelContainer
      isOpen={isOpen}
      onRenderFooterContent={onRenderFooterContent}
      headerText={i18n.getString(_TL_('Edit logo'))}
      dismiss={dismiss}
      className="logo-edit-panel"
    >
      <Body1>
        {i18n.getString(_TL_('Customize how your logo looks across potential placements.'))}
      </Body1>
      <div className={mergeClasses('aspect-ratio', classes.aspectRatio)}>
        <Field label={i18n.getString(_TL_('Which type of logo format would you like to save this as?'))}>
          <RadioGroup value={aspectRatio} onChange={(_, data) => updateAspectRation(data.value)}>
            <Radio value={LogoFormat.square} disabled={!sqaureEnabled} label={i18n.getString(_TL_('Square (1:1)'))} />
            <Radio value={LogoFormat.landscape} disabled={!landscapeEnabled} label={i18n.getString(_TL_('Landscape (4:1)'))} />
          </RadioGroup>
        </Field>
      </div>
      <div className={classes.imageEditorContainer}>
        {image ?
          <ImageCropWithBoundingBox
            key={aspectRatio}
            baseImage={image}
            cropSettings={currentCropSettings}
            onCropSettingsChanged={setCurrentCropSettings}
            minCropSize={[aspectRatio === LogoFormat.square ? MinLogoSize : MinLogoSize * 4, MinLogoSize]}
            maxWidth={600}
            maxHeight={maxCropHeight}
            aspectRatio={aspectRatio === LogoFormat.square ? 1 : 4}
          />
        : <Spinner className={classes.spinner} />}
      </div>
      {showBackgroundSettings && <div className={classes.background}>
        <Body1>
          {i18n.getString(_TL_('What type of backgrounds should this logo be used with?'))}
        </Body1>
        <Checkbox
          className="light-background-checkbox"
          checked={useInLightBackground}
          onChange={(_, data) => setUseInLightBackground(data.checked)}
          label={i18n.getString(_TL_('Light background'))}
        />
        <Checkbox
          className="dark-background-checkbox"
          checked={useInDarkBackground}
          onChange={(_, data) => setUseInDarkBackground(data.checked)}
          label={i18n.getString(_TL_('Dark background'))}
        />
                                 </div>
      }
    </AdsStudioPanelContainer>
  );
};

BrandKitLogoEditPanel.propTypes = {
  i18n: PropTypes.shape({ getString: func }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  dismiss: PropTypes.func.isRequired,
  hasLandScapeLimit: PropTypes.bool,
  minLogoSize: PropTypes.number,
  hasSquareLimit: PropTypes.bool,
  format: PropTypes.string.isRequired,
  lightBackground: PropTypes.bool.isRequired,
  showBackgroundSettings: PropTypes.bool,
  darkBackground: PropTypes.bool.isRequired,
  image: PropTypes.shape({ Height: PropTypes.number, Width: PropTypes.number }),
  cropSettings: PropTypes.shape({
    SourceX: PropTypes.number,
    SourceY: PropTypes.number,
    SourceWidth: PropTypes.number,
    SourceHeight: PropTypes.number,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

BrandKitLogoEditPanel.defaultProps = {
  image: null,
  minLogoSize: 128,
  hasLandScapeLimit: true,
  hasSquareLimit: true,
  showBackgroundSettings: false,
};

